import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { toast } from '../../../../shared/utils/toast';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';
import {
  ImagingAgentService,
  ImagingConfig,
  ImagingStatus,
  IMAGING_DATABASE_FORMATS,
  imagingErrorKey,
  imagingProbeIsFree,
} from '../../../../core/services/imaging-agent.service';

/**
 * Settings -> Imaging (RX): configures the LOCAL agent's imaging setup. All
 * state here is per MACHINE (the agent's imaging-config.json on this
 * computer), unlike the other settings tabs, which hold clinic-wide data -
 * that scope difference is why this is its own tab. The agent itself is
 * installed from the Executables tab.
 */
@Component({
  selector: 'app-imaging-tab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    InputTextModule,
    AppButton,
    HasPermissionDirective,
  ],
  templateUrl: './imaging-tab.html',
})
export class ImagingTab implements OnInit {
  private translate = inject(TranslateService);
  private perm = inject(PermissionService);
  private imaging = inject(ImagingAgentService);

  get canEdit(): boolean {
    return this.perm.canEdit(MODULE.CONFIGURACOES);
  }

  imagingStatus: ImagingStatus | null = null;
  imagingConfig: ImagingConfig | null = null;
  imagingForm = { executablePath: '', patientRepository: '', databaseFormat: '' };
  // SIDEXIS is configured separately because a clinic PC can run BOTH vendors -
  // one form per vendor, each saving on its own, so setting up one can never
  // overwrite the other.
  sidexisConfig: ImagingConfig | null = null;
  sidexisForm = {
    executablePath: '',
    mailboxPath: '',
    stationName: '',
    patientIdSource: '',
    sironaIniPath: '',
  };
  imagingLoading = true;
  imagingSaving = false;
  sidexisSaving = false;
  // Vendors the user asked to add during THIS visit to the tab. A clinic runs
  // one or two imaging systems out of however many the agent can drive, so a
  // section it has never set up is offered as an "add" button instead of a full
  // form standing there looking like unfinished setup. Not persisted: a vendor
  // becomes real by being saved, and reopening the tab shows the truth again.
  private revealed = new Set<string>();
  // Browser only: the agent is NOT probed on tab open (same Local Network
  // Access rule as consultations - a Block earned here breaks RX everywhere on
  // this origin). The user starts the check explicitly instead.
  probeDeferred = false;
  readonly imagingFormats = IMAGING_DATABASE_FORMATS;

  ngOnInit() {
    if (imagingProbeIsFree()) {
      void this.loadImaging();
    } else {
      this.probeDeferred = true;
      this.imagingLoading = false;
    }
  }

  async loadImaging() {
    this.probeDeferred = false;
    this.imagingLoading = true;
    this.imagingStatus = await this.imaging.getStatus();
    const all = this.imagingStatus.reachable ? await this.imaging.getConfig() : null;
    // null here means this agent build has no driver for the vendor, which
    // hides the section for good: a form whose every Save comes back
    // UNSUPPORTED_SYSTEM is worse than no form, and against an older agent that
    // is exactly what SIDEXIS would give. A vendor the build DOES support but
    // this machine has not set up is a different case, handled by vendorShown
    // and the add row - not by an empty form nobody asked for.
    this.imagingConfig =
      all && this.imagingStatus.vendors['carestream']?.available ? all['carestream'] || {} : null;
    this.sidexisConfig =
      all && this.imagingStatus.vendors['sidexis']?.available ? all['sidexis'] || {} : null;
    this.imagingForm = {
      executablePath: this.imagingConfig?.executablePath || '',
      patientRepository: this.imagingConfig?.patientRepository || '',
      databaseFormat: this.imagingConfig?.databaseFormat || '',
    };
    this.sidexisForm = {
      executablePath: this.sidexisConfig?.executablePath || '',
      mailboxPath: this.sidexisConfig?.mailboxPath || '',
      stationName: this.sidexisConfig?.stationName || '',
      patientIdSource: this.sidexisConfig?.patientIdSource || '',
      sironaIniPath: this.sidexisConfig?.sironaIniPath || '',
    };
    this.imagingLoading = false;
  }

  /** Per-vendor readiness, for the chips. */
  vendorConfigured(system: string): boolean {
    return Boolean(this.imagingStatus?.vendors?.[system]?.configured);
  }

  /** Can this agent build drive the vendor at all? */
  private vendorAvailable(system: string): boolean {
    return Boolean(this.imagingStatus?.vendors?.[system]?.available);
  }

  /**
   * Show a vendor's form when this computer uses it, when setup is half done,
   * or when the user just asked to add it. The half-done case matters: a vendor
   * with a path filled but a field still missing reports NOT configured, and
   * hiding it would leave no way to finish.
   */
  vendorShown(system: string): boolean {
    if (!this.vendorAvailable(system)) return false;
    if (this.vendorConfigured(system) || this.revealed.has(system)) return true;
    const saved = system === 'sidexis' ? this.sidexisConfig : this.imagingConfig;
    return Object.values(saved || {}).some((v) => typeof v === 'string' && v.length > 0);
  }

  /** Vendors this build supports that the clinic has not set up - the add row. */
  get addableVendors(): string[] {
    const known = Object.keys(this.imagingStatus?.vendors || {});
    return known.filter((v) => this.vendorAvailable(v) && !this.vendorShown(v)).sort();
  }

  /**
   * The add row's wording. Nothing set up yet is the first-run question ("which
   * system does this computer use?"); with a section already on screen the same
   * buttons mean "another one as well".
   */
  get addVendorHintKey(): string {
    const known = Object.keys(this.imagingStatus?.vendors || {});
    return known.some((v) => this.vendorShown(v))
      ? 'imaging_add_vendor_hint'
      : 'imaging_pick_vendor_hint';
  }

  revealVendor(system: string) {
    this.revealed.add(system);
  }

  // Only the fields that actually changed are sent: the agent validates each
  // key it receives (a reachability probe per path), so re-sending an untouched
  // path costs a needless SMB round trip.
  private changedFields(form: Record<string, string>, saved: ImagingConfig | null) {
    const updates: Record<string, string> = {};
    for (const key of Object.keys(form)) {
      const current = (saved as any)?.[key] || '';
      if (form[key] !== current) updates[key] = form[key];
    }
    return updates;
  }

  get imagingDirty(): boolean {
    if (!this.imagingConfig) return false;
    return Object.keys(this.changedFields(this.imagingForm, this.imagingConfig)).length > 0;
  }

  get sidexisDirty(): boolean {
    if (!this.sidexisConfig) return false;
    return Object.keys(this.changedFields(this.sidexisForm, this.sidexisConfig)).length > 0;
  }

  async saveImagingConfig() {
    if (!this.canEdit || this.imagingSaving || !this.imagingConfig) return;
    const updates = this.changedFields(this.imagingForm, this.imagingConfig);
    if (Object.keys(updates).length === 0) return;

    this.imagingSaving = true;
    const result = await this.imaging.saveConfig('carestream', updates);
    this.imagingSaving = false;
    if (result.ok) {
      toast.success(this.translate.instant('imaging_config_saved'));
      this.imagingConfig = result.config;
      // Re-seed the form from what the agent ACTUALLY stored, not from what was
      // typed: the agent normalizes values, so a field would otherwise stay
      // "dirty" forever and the Save button would keep offering to save a
      // change that has already been made.
      this.imagingForm = {
        executablePath: result.config.executablePath || '',
        patientRepository: result.config.patientRepository || '',
        databaseFormat: result.config.databaseFormat || '',
      };
      // re-check status so the "configured" chip flips without a reload
      this.imagingStatus = await this.imaging.getStatus();
    } else {
      toast.error(this.translate.instant(imagingErrorKey(result.code)));
    }
  }

  async saveSidexisConfig() {
    if (!this.canEdit || this.sidexisSaving || !this.sidexisConfig) return;
    const updates = this.changedFields(this.sidexisForm, this.sidexisConfig);
    if (Object.keys(updates).length === 0) return;

    this.sidexisSaving = true;
    const result = await this.imaging.saveConfig('sidexis', updates);
    this.sidexisSaving = false;
    if (result.ok) {
      toast.success(this.translate.instant('imaging_config_saved'));
      this.sidexisConfig = result.config;
      // Same reason as Carestream above: the agent normalizes what it stores
      // (an empty string clears a field, and it decides the effective value),
      // so re-seeding from the response is what keeps the Save button honest.
      this.sidexisForm = {
        executablePath: result.config.executablePath || '',
        mailboxPath: result.config.mailboxPath || '',
        stationName: result.config.stationName || '',
        patientIdSource: result.config.patientIdSource || '',
        sironaIniPath: result.config.sironaIniPath || '',
      };
      this.imagingStatus = await this.imaging.getStatus();
    } else {
      toast.error(this.translate.instant(imagingErrorKey(result.code)));
    }
  }
}
